import { type AxialCoord, hexDistance, hexKey } from "./hex";
import { hexNoise } from "./map";
import { percentOf } from "./percent";
import { rankBy } from "./pick";
import type { Terrain } from "./types/state";

/**
 * The land's second reading (ADR-0014 slice M2, docs/design/MULTIPLAYER-ROLES-IDEA.md, "Money"):
 * a hidden layer of finds under the map the King reads for war. It is a pure function of the
 * run's seed and the land, never stored, so every machine sees the same veins; a run keeps
 * only which hexes are spent. Finds follow seeded noise, so they lie in veins, and the share
 * of the map that holds one is fixed by rank, not by a threshold, so no seed is a desert.
 */
export interface FindRules {
  /** Percent of the map's hexes that hold a find. */
  readonly sharePercent: number;
  /** Hexes per noise feature of the hidden layer: the width of a vein. */
  readonly veinScale: number;
  /** A rough hex's richness in percent of an open one's before the ranking; 100 is no favour. */
  readonly roughPercent: number;
  /** The richest `richPercent` of the finds are worth 3, the ones down to `fairPercent` 2, the rest 1. */
  readonly richPercent: number;
  readonly fairPercent: number;
  /** Ticks a soldier stands idle, no enemy beside it, to learn what its hex holds. */
  readonly prospectTicks: number;
  /** What a find worth 1 pays in the city it lay in. */
  readonly basePrice: number;
  /** The price grows by 100 % per `cityReach` hexes between the find's hex and the city, up to this. */
  readonly maxPricePercent: number;
  /** Percent of the price the city keeps into its kingdom's pool. */
  readonly marketCutPercent: number;
  /** A carrying soldier steps once in this many move intervals. */
  readonly carryMoveIntervals: number;
}

/** What a soldier carries: the find's worth and the hex it lay on. */
export interface Load {
  readonly value: number;
  readonly from: AxialCoord;
}

/** The part of a hex the layer reads: both are fixed for the whole run. */
export interface Land {
  readonly coord: AxialCoord;
  readonly terrain: Terrain;
}

/** Mixed into the run's seed so the veins share nothing with the map's own noise. */
const FINDS_SALT = 0x5f1d3a97;
const RICH_VALUE = 3;
const FAIR_VALUE = 2;
const POOR_VALUE = 1;

/**
 * The whole layer: hex key to the worth of the find there, 1 to 3. The hexes are ranked by
 * their noise (rough ground lifted by `roughPercent`), ties to map order, and the best
 * `sharePercent` of them hold a find.
 */
export function findsField(
  seed: number,
  land: readonly Land[],
  rules: FindRules,
): ReadonlyMap<string, number> {
  const salt = (seed ^ FINDS_SALT) >>> 0;
  const count = Math.round((land.length * rules.sharePercent) / 100);
  const ranked = rankBy(
    land,
    (hex) =>
      -(
        hexNoise(hex.coord, rules.veinScale, salt) *
        (hex.terrain === "rough" ? rules.roughPercent : 100)
      ),
  ).slice(0, count);
  const field = new Map<string, number>();
  ranked.forEach((hex, rank) => {
    const value =
      rank * 100 < count * rules.richPercent
        ? RICH_VALUE
        : rank * 100 < count * rules.fairPercent
          ? FAIR_VALUE
          : POOR_VALUE;
    field.set(hexKey(hex.coord), value);
  });
  return field;
}

/**
 * What a city pays for a load: its worth times the base price, 100 % more per `cityReach`
 * hexes the find lay from the city, never above `maxPricePercent`. Distance is the price, so
 * the economy has a geography and the war decides which roads are worth it.
 */
export function loadPrice(
  rules: FindRules,
  cityReach: number,
  load: Load,
  city: AxialCoord,
): number {
  const carried = hexDistance(load.from, city);
  const percent = Math.min(
    rules.maxPricePercent,
    100 + Math.floor((100 * carried) / Math.max(1, cityReach)),
  );
  return percentOf(load.value * rules.basePrice, percent);
}
